import { useEffect, useMemo, useRef } from 'react';

import debounce from 'lodash.debounce';

/**
 * Returns a memoized debounced function that delays invoking callback until
 * the provided interval in milliseconds has elapsed.
 *
 * After unmount the debounced function becomes inert: pending trailing
 * invocations are canceled and later calls are ignored. Without this,
 * consumers like usePlacesAutocomplete fired network requests and setState
 * from timers that outlived the component (and, under Jest, after the test
 * environment was torn down).
 *
 * @param callback: Function that needs to be invoked after the interval
 * @param wait: Interval in milliseconds
 */
export const useDebounceFn = (callback: (...args: readonly any[]) => unknown, wait = 300) => {
  const isMounted = useRef(true);

  const debounced = useMemo(() => debounce(callback, wait), [callback, wait]);

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  // Cancel any pending trailing invocation when the debounced instance is
  // replaced or the component unmounts.
  useEffect(() => {
    return () => {
      debounced.cancel();
    };
  }, [debounced]);

  return useMemo(() => {
    const guarded = (...args: readonly any[]) => {
      if (!isMounted.current) return undefined;

      return debounced(...args);
    };

    guarded.cancel = debounced.cancel.bind(debounced);
    guarded.flush = debounced.flush.bind(debounced);

    return guarded;
  }, [debounced]);
};
